import { useEffect, useState } from 'react';

import { LocationNodeData, NodeType, StructureResponse } from '../../../@types';
import { DropdownItem } from '../../../components/atoms/SelectMenu';
import { StructureTypeCategory } from '../../../configs/enums';
import { NONE_ITEM } from '../../../constants/common';
import {
  useGetNodeChildrenByNodeType,
  useGetNodeSubTree,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
} from '../../../queries/structure-query';

const toSingleMenu = (node: StructureResponse): DropdownItem => ({
  value: node.id.toString(),
  label: node.name,
  disabled: !node.isActive,
});

const noneItem = (): DropdownItem[] => NONE_ITEM.map((i) => ({ ...i, disabled: false }));

const resolveParentId = (clientId: number, contractId: number, brandId: number): string => {
  if (brandId > 0) return brandId.toString();
  if (contractId > 0) return contractId.toString();
  return clientId.toString();
};

const buildSelection = (
  clientNode: StructureResponse,
  contractNode: StructureResponse | undefined,
  brandNode: StructureResponse | undefined
): ClientHierarchySelection => {
  const nextContractId = contractNode?.id ?? 0;
  const nextBrandId = brandNode?.id ?? 0;
  const brandSelectionId = nextBrandId > 0 ? nextBrandId.toString() : '0';
  return {
    clientId: clientNode.id,
    contractId: nextContractId,
    brandId: nextBrandId,
    clientHierarchy: `${clientNode.name}/${contractNode?.name || ''}/${brandNode?.name || ''}`,
    parentId: resolveParentId(clientNode.id, nextContractId, nextBrandId),
    brandSelectionId,
  };
};

export interface ClientHierarchySelection {
  clientId: number;
  contractId: number;
  brandId: number;
  clientHierarchy: string;
  parentId: string;
  brandSelectionId: string;
}

const EMPTY_SELECTION: ClientHierarchySelection = {
  clientId: 0,
  contractId: 0,
  brandId: 0,
  clientHierarchy: '',
  parentId: '',
  brandSelectionId: '0',
};

export interface UseClientHierarchyResult {
  clients: DropdownItem[];
  contracts: DropdownItem[];
  brands: DropdownItem[];
  selection: ClientHierarchySelection;
  onChangeNodeType: (nodeType: NodeType, parentId: string) => void;
  onParentSelect: (nodeId: number) => void;
  syncFromLocationTree: (mainLocationNode?: LocationNodeData) => void;
  syncFromLocationId: (locationId: number) => void;
  reset: () => void;
}

export const useClientHierarchy = (): UseClientHierarchyResult => {
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);
  const [selection, setSelection] = useState<ClientHierarchySelection>(EMPTY_SELECTION);
  const [queryNodeType, setQueryNodeType] = useState(NodeType.Client);
  const [queryParentId, setQueryParentId] = useState('');
  const [syncLocationId, setSyncLocationId] = useState<number>();

  const clientStructuresQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Client
  );
  const childrenQuery = useGetNodeChildrenByNodeType(queryParentId, queryNodeType);
  const subTreeQuery = useGetNodeSubTree(syncLocationId?.toString() || '0');

  useEffect(() => {
    clientStructuresQuery.refetch();
  }, []);

  useEffect(() => {
    if (!clientStructuresQuery.data) return;
    setClients(
      clientStructuresQuery.data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
        disabled: !item.isActive,
      }))
    );
  }, [clientStructuresQuery.data]);

  useEffect(() => {
    if (queryParentId && queryNodeType) childrenQuery.refetch();
  }, [queryParentId, queryNodeType]);

  useEffect(() => {
    if (!childrenQuery.data) return;
    const menu = childrenQuery.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
      disabled: !item.isActive,
    }));
    const withNone: DropdownItem[] = noneItem().concat(menu);
    if (queryNodeType === NodeType.Contract) setContracts(withNone);
    if (queryNodeType === NodeType.Brand) setBrands(withNone);
  }, [childrenQuery.data]);

  useEffect(() => {
    if (syncLocationId && syncLocationId > 0) subTreeQuery.refetch();
  }, [syncLocationId]);

  useEffect(() => {
    if (!subTreeQuery.data) return;
    const clientNode = subTreeQuery.data.find((n) => n.nodeTypeCode === NodeType.Client);
    const contractNode = subTreeQuery.data.find((n) => n.nodeTypeCode === NodeType.Contract);
    const brandNode = subTreeQuery.data.find((n) => n.nodeTypeCode === NodeType.Brand);
    if (!clientNode) return;
    setSelection(buildSelection(clientNode, contractNode, brandNode));
    setClients([toSingleMenu(clientNode)]);
    if (contractNode) {
      setContracts(noneItem().concat([toSingleMenu(contractNode)]));
    }
    if (brandNode) {
      setBrands(noneItem().concat([toSingleMenu(brandNode)]));
    }
  }, [subTreeQuery.data]);

  const onChangeNodeType = (nodeType: NodeType, parentId: string) => {
    setQueryNodeType(nodeType);
    setQueryParentId(parentId);
    if (nodeType === NodeType.Contract) {
      setSelection((prev) => ({ ...prev, clientId: Number(parentId), parentId }));
    }
  };

  const onParentSelect = (nodeId: number) => {
    setSelection((prev) => {
      const brandSelectionId = nodeId > 0 ? nodeId.toString() : '0';
      const parentId = brandSelectionId !== '0' ? brandSelectionId : prev.parentId;
      return { ...prev, brandId: nodeId, brandSelectionId, parentId };
    });
  };

  const syncFromLocationTree = (mainLocationNode?: LocationNodeData) => {
    if (mainLocationNode?.id) setSyncLocationId(mainLocationNode.id);
  };

  const syncFromLocationId = (locationId: number) => {
    if (locationId > 0) setSyncLocationId(locationId);
  };

  const reset = () => {
    setSelection(EMPTY_SELECTION);
    setContracts([]);
    setBrands([]);
    setSyncLocationId(undefined);
    if (clientStructuresQuery.data) {
      setClients(
        clientStructuresQuery.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive,
        }))
      );
    }
  };

  return {
    clients,
    contracts,
    brands,
    selection,
    onChangeNodeType,
    onParentSelect,
    syncFromLocationTree,
    syncFromLocationId,
    reset,
  };
};
